import {
  ExternalLink,
  CalendarPlus,
  Copy,
  Check,
  Video,
  FileText,
  RefreshCw,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import type { MeetingDto } from "@shared/types/MeetingDto";
import { STATUS_LABELS, STATUS_COLORS, TYPE_LABELS } from "./constants";

interface MeetingDetailSheetProps {
  meeting: MeetingDto | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReschedule: (meeting: MeetingDto) => void;
  onCancel: (meeting: MeetingDto) => void;
}

export function MeetingDetailSheet({
  meeting,
  open,
  onOpenChange,
  onReschedule,
  onCancel,
}: MeetingDetailSheetProps) {
  const [copied, setCopied] = useState(false);

  if (!meeting) return null;

  const scheduledDate = meeting.scheduledAt
    ? new Date(meeting.scheduledAt).toLocaleString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : null;

  const handleCopyMagicLink = async () => {
    if (!meeting.magicLink) return;
    await navigator.clipboard.writeText(meeting.magicLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleAddToCalendar = () => {
    if (!meeting.scheduledAt) return;
    const start = new Date(meeting.scheduledAt);
    const end = new Date(
      start.getTime() + (meeting.durationMinutes || 30) * 60000
    );
    const formatDate = (d: Date) =>
      d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(meeting.title)}&dates=${formatDate(start)}/${formatDate(end)}`;
    window.open(url, "_blank");
  };

  const statusColor = STATUS_COLORS[meeting.status] || "bg-muted text-muted-foreground";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        aria-label={`Meeting details: ${meeting.title}`}
      >
        <SheetHeader>
          <div className="flex items-start justify-between gap-2 pr-6">
            <div className="space-y-1">
              <SheetTitle>{meeting.title}</SheetTitle>
              <SheetDescription>
                {meeting.displayName || meeting.email || "No participant info"}
              </SheetDescription>
            </div>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Badge className={statusColor}>
              {STATUS_LABELS[meeting.status]}
            </Badge>
            <Badge variant="outline">{TYPE_LABELS[meeting.type]}</Badge>
          </div>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              Schedule
            </h4>
            {scheduledDate ? (
              <div className="space-y-1">
                <p className="text-sm font-medium">{scheduledDate}</p>
                <p className="text-sm text-muted-foreground">
                  {meeting.durationMinutes} min
                  {meeting.timezone && ` · ${meeting.timezone}`}
                </p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Not yet scheduled
              </p>
            )}
          </div>

          <Separator />

          {(meeting.displayName || meeting.email || meeting.company) && (
            <>
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                  Participant
                </h4>
                <div className="space-y-1 text-sm">
                  {meeting.displayName && (
                    <p className="font-medium">{meeting.displayName}</p>
                  )}
                  {meeting.email && (
                    <p className="text-muted-foreground">{meeting.email}</p>
                  )}
                  {meeting.company && (
                    <p className="text-muted-foreground">{meeting.company}</p>
                  )}
                </div>
              </div>
              <Separator />
            </>
          )}

          {meeting.clientActionItems && meeting.clientActionItems.length > 0 && (
            <>
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                  Action items
                </h4>
                <ul className="space-y-1.5">
                  {meeting.clientActionItems.map((item, i) => (
                    <li
                      key={i}
                      className="text-sm flex items-start gap-2"
                    >
                      <span className="text-muted-foreground mt-0.5">·</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <Separator />
            </>
          )}

          {meeting.attachedDocs && meeting.attachedDocs.length > 0 && (
            <>
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                  Attachments
                </h4>
                <div className="space-y-2">
                  {meeting.attachedDocs.map((doc, i) => (
                    <a
                      key={i}
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-primary hover:underline"
                    >
                      <FileText className="h-3.5 w-3.5" />
                      {doc.name}
                    </a>
                  ))}
                </div>
              </div>
              <Separator />
            </>
          )}

          {meeting.status === "AWAITING_BOOKING" && meeting.magicLink && (
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 space-y-3">
              <p className="text-sm font-medium">
                Pick a time to confirm this meeting.
              </p>
              <div className="flex items-center gap-2">
                <Button size="sm" asChild>
                  <a
                    href={meeting.magicLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                    Schedule now
                  </a>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCopyMagicLink}
                >
                  {copied ? (
                    <Check className="h-3.5 w-3.5" />
                  ) : (
                    <Copy className="h-3.5 w-3.5" />
                  )}
                </Button>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2">
            {meeting.meetingUrl && meeting.status === "CONFIRMED" && (
              <Button size="sm" asChild>
                <a
                  href={meeting.meetingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Video className="h-3.5 w-3.5 mr-1.5" />
                  Join meeting
                </a>
              </Button>
            )}

            {meeting.recordingUrl && (
              <Button size="sm" variant="outline" asChild>
                <a
                  href={meeting.recordingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Video className="h-3.5 w-3.5 mr-1.5" />
                  View recording
                </a>
              </Button>
            )}

            {meeting.scheduledAt && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleAddToCalendar}
              >
                <CalendarPlus className="h-3.5 w-3.5 mr-1.5" />
                Add to calendar
              </Button>
            )}

            {!["CANCELLED", "COMPLETED", "NO_SHOW"].includes(
              meeting.status
            ) && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onReschedule(meeting)}
                >
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                  Reschedule
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-destructive hover:text-destructive"
                  onClick={() => onCancel(meeting)}
                >
                  <XCircle className="h-3.5 w-3.5 mr-1.5" />
                  Cancel
                </Button>
              </>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
